// Thin domain-specific API surface. Components import these named functions —
// nobody calls fetch() directly.
import { apiFetch, getApiBase } from './http';
export const api = {
    health: () => apiFetch('/health'),
    me: () => apiFetch('/me'),
    logout: () => apiFetch('/auth/logout', {
        method: 'POST',
        body: {},
    }),
    // Cameras
    listCameras: () => apiFetch('/cameras'),
    addCamera: (body) => apiFetch('/cameras', { method: 'POST', body }),
    startCamera: (id) => apiFetch(`/cameras/${encodeURIComponent(id)}/start`, { method: 'POST', body: {} }),
    stopCamera: (id) => apiFetch(`/cameras/${encodeURIComponent(id)}/stop`, { method: 'POST', body: {} }),
    listCameraStreamHealth: (cameraId) => {
        const qs = cameraId ? `?camera_id=${encodeURIComponent(cameraId)}` : '';
        return apiFetch(`/cameras/stream-health${qs}`);
    },
    deleteCamera: (id) => apiFetch(`/cameras/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    getCameraPlaybackUrl: (id) => apiFetch(`/cameras/${encodeURIComponent(id)}/playback-url`),
    // Workflow
    summarize: (body, signal) => apiFetch('/summarize', {
        method: 'POST',
        body,
        timeoutMs: 240_000,
        signal,
    }),
    chat: (body, signal) => apiFetch('/chat', { method: 'POST', body, timeoutMs: 240_000, signal }),
    search: (body, signal) => apiFetch('/search', { method: 'POST', body, timeoutMs: 240_000, signal }),
    // Personas
    listPersonas: () => apiFetch('/personas'),
    upsertPersona: (body) => apiFetch('/personas', { method: 'POST', body }),
    deletePersona: (name) => apiFetch(`/personas/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    // Async LVS jobs (workflow=summary)
    listJobs: (filter) => {
        const qs = new URLSearchParams();
        if (filter?.persona)
            qs.set('persona', filter.persona);
        if (filter?.status)
            qs.set('status', filter.status);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return apiFetch(`/jobs${suffix}`);
    },
    getJob: (jobId) => apiFetch(`/jobs/${encodeURIComponent(jobId)}`),
    cancelJob: (jobId) => apiFetch(`/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE' }),
    // Warehouse dashboard workflow
    listWarehouseVideos: () => apiFetch('/warehouse/videos'),
    getWarehouseDashboard: (videoId) => {
        const qs = videoId ? `?video_id=${encodeURIComponent(videoId)}` : '';
        return apiFetch(`/warehouse/dashboard${qs}`);
    },
    analyzeWarehouseVideo: (videoId, body) => apiFetch(`/warehouse/analyze/${encodeURIComponent(videoId)}`, { method: 'POST', body }),
    getWarehouseJob: (jobId) => apiFetch(`/warehouse/jobs/${encodeURIComponent(jobId)}`),
    cancelWarehouseJob: (jobId) => apiFetch(`/warehouse/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE' }),
    // Redaction (separate JobsService on the API; runs on the VM redaction-service)
    submitRedaction: (cameraId, options) => apiFetch(`/cameras/${encodeURIComponent(cameraId)}/redact`, { method: 'POST', body: options ?? {} }),
    listRedactJobs: (status) => {
        const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
        return apiFetch(`/redact/jobs${suffix}`);
    },
    getRedactJob: (jobId) => apiFetch(`/redact/jobs/${encodeURIComponent(jobId)}`),
    cancelRedactJob: (jobId) => apiFetch(`/redact/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE' }),
    redactHealth: () => apiFetch('/redact/health'),
    // Standalone Brev-style redaction flow (upload → run → preview → download)
    uploadRedact: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return apiFetch('/redact/upload', { method: 'POST', body: fd, timeoutMs: 6 * 60 * 60 * 1000 });
    },
    listRedactInputs: () => apiFetch('/redact/inputs'),
    listRedactOutputs: () => apiFetch('/redact/outputs'),
    submitRedactRun: (body) => apiFetch('/redact/run', { method: 'POST', body }),
    getRedactRun: (jobId) => apiFetch(`/redact/run/${encodeURIComponent(jobId)}`),
    redactInputUrl: (name) => `${getApiBase()}/api/redact/inputs/${encodeURIComponent(name)}`,
    redactOutputUrl: (name) => `${getApiBase()}/api/redact/outputs/${encodeURIComponent(name)}`,
    // Analytics
    liveAlerts: (cameraId) => apiFetch(`/analytics/alerts/live${cameraId ? `?camera_id=${encodeURIComponent(cameraId)}` : ''}`),
    getUploadedStructuredAnalytics: (cameraId) => apiFetch(`/analytics/uploads/${encodeURIComponent(cameraId)}/structured`),
    getUploadedStructuredConfig: (cameraId) => apiFetch(`/analytics/uploads/${encodeURIComponent(cameraId)}/structured/config`),
    updateUploadedStructuredConfig: (cameraId, body) => apiFetch(`/analytics/uploads/${encodeURIComponent(cameraId)}/structured/config`, { method: 'POST', body }),
    runUploadedStructuredAnalytics: (cameraId, body) => apiFetch(`/analytics/uploads/${encodeURIComponent(cameraId)}/structured/run`, { method: 'POST', body: body ?? { trigger_vss: true }, timeoutMs: 5 * 60 * 1000 }),
    // Admin settings (defaults / retention / notifications / language)
    getSettings: () => apiFetch('/admin/settings'),
    updateSettings: (patch) => apiFetch('/admin/settings', {
        method: 'PUT',
        body: patch,
    }),
};
